import { useState } from 'react'
import { useMutation } from '@tanstack/react-query'
import { KeyRound } from 'lucide-react'
import { api, errorMessage } from '@/lib/api'
import Button from '@/components/ui/Button'
import Modal from '@/components/ui/Modal'
import { Field, PasswordInput } from '@/components/ui/Field'
import { useToast } from '@/components/ui/toast'

interface Account {
  name: string
  email: string
  /** The PUT endpoint that sets this account's password. */
  endpoint: string
}

/**
 * An admin types a password in for someone else's login: a team member on
 * Staff, a warehouse partner's login on Partners, or a customer on Customers. Needed because the
 * emailed link only arrives once the mail domain is verified.
 */
export default function SetPasswordModal({ account, onClose }: { account: Account | null; onClose: () => void }) {
  const toast = useToast()
  const [password, setPassword] = useState('')
  const [passwordConfirm, setPasswordConfirm] = useState('')

  const close = () => {
    setPassword('')
    setPasswordConfirm('')
    onClose()
  }

  const setPasswordMutation = useMutation({
    mutationFn: async (endpoint: string) =>
      (await api.put(endpoint, { password, password_confirmation: passwordConfirm })).data,
    onSuccess: (res: { message?: string }) => {
      close()
      toast.success('Password set', res.message)
    },
    // The dialog stays open on failure so a typo can be fixed in place.
    onError: (err) => toast.error('Could not set the password', errorMessage(err)),
  })

  const passwordReady = password.length > 0 && password === passwordConfirm

  return (
    <Modal
      open={!!account}
      onClose={close}
      title={account ? `Set a password for ${account.name}` : ''}
      width="max-w-sm"
      footer={
        <>
          <Button variant="outline" onClick={close} disabled={setPasswordMutation.isPending}>
            Cancel
          </Button>
          <Button
            loading={setPasswordMutation.isPending}
            disabled={!passwordReady}
            onClick={() => account && setPasswordMutation.mutate(account.endpoint)}
          >
            <KeyRound className="size-4" /> Set password
          </Button>
        </>
      }
    >
      {account && (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault()
            if (passwordReady) setPasswordMutation.mutate(account.endpoint)
          }}
        >
          {/* Lets password managers file it under the right account. */}
          <input type="text" name="username" autoComplete="username" value={account.email} readOnly hidden />
          <Field label="New password" htmlFor="member-password">
            <PasswordInput
              id="member-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              autoFocus
            />
          </Field>
          <Field
            label="Confirm new password"
            htmlFor="member-password-confirm"
            error={passwordConfirm && password !== passwordConfirm ? 'The two passwords do not match.' : undefined}
          >
            <PasswordInput
              id="member-password-confirm"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              autoComplete="new-password"
            />
          </Field>
          <p className="text-xs text-foreground/60">
            At least 8 characters. {account.name} is signed out on every device and uses this password from now on.
            Tell them in person - it is not emailed.
          </p>
        </form>
      )}
    </Modal>
  )
}
